/**
 * A live readout of what the editor is doing, for when it stutters and nobody
 * can see why. Enabled with `?debug` in the URL; costs nothing otherwise.
 *
 * It exists because four fixes to the playback engine were made from headless
 * measurements — no GPU, no compositor, no audio, no React tree — and each was
 * declared to have worked. The only measurement that counts is the one taken
 * on the machine where it stutters. This panel takes it.
 *
 * Everything is written straight to the DOM, so the panel cannot itself be the
 * thing it is measuring.
 */

export interface FrameSample {
  playhead: number;
  master: HTMLVideoElement | HTMLMediaElement | null;
  clockLeads: boolean;
  audioState: string;
}

interface Quality {
  droppedVideoFrames: number;
  totalVideoFrames: number;
}

const LONG_FRAME_MS = 50;

export class Diagnostics {
  private readonly panel: HTMLPreElement;
  private frames = 0;
  private longFrames = 0;
  private renders = 0;
  private lastTick = performance.now();
  private windowStart = performance.now();
  private worst = 0;
  private durations: number[] = [];
  private seeks = 0;
  private seekListenerOn: HTMLMediaElement | null = null;

  static enabled(): boolean {
    return typeof location !== "undefined" && new URLSearchParams(location.search).has("debug");
  }

  constructor() {
    this.panel = document.createElement("pre");
    this.panel.setAttribute("aria-hidden", "true");
    this.panel.style.cssText = [
      "position:fixed",
      "right:12px",
      "bottom:12px",
      "z-index:9999",
      "margin:0",
      "padding:10px 12px",
      "background:rgba(20,16,12,.92)",
      "color:#e9dfcd",
      "font:11px/1.45 ui-monospace,Menlo,Consolas,monospace",
      "border-radius:10px",
      "pointer-events:none",
      "white-space:pre",
      "max-width:46ch",
    ].join(";");
    document.body.appendChild(this.panel);
  }

  /** Call once per React commit of the editor. */
  rendered(): void {
    this.renders += 1;
  }

  /** Call once per animation frame, after the frame's work is done. */
  frame(sample: FrameSample): void {
    const now = performance.now();
    const took = now - this.lastTick;
    this.lastTick = now;
    this.frames += 1;
    this.durations.push(took);
    if (took > LONG_FRAME_MS) this.longFrames += 1;
    if (took > this.worst) this.worst = took;

    if (sample.master && sample.master !== this.seekListenerOn) {
      this.seekListenerOn?.removeEventListener("seeking", this.onSeeking);
      sample.master.addEventListener("seeking", this.onSeeking);
      this.seekListenerOn = sample.master;
    }

    // Report twice a second; more often and the text itself flickers.
    if (now - this.windowStart < 500) return;
    const seconds = (now - this.windowStart) / 1000;
    const sorted = [...this.durations].sort((a, b) => a - b);
    const p95 = sorted[Math.floor(sorted.length * 0.95)] ?? 0;
    const el = sample.master;
    const video = el as HTMLVideoElement | null;
    const quality: Quality | null =
      video && typeof video.getVideoPlaybackQuality === "function"
        ? video.getVideoPlaybackQuality()
        : null;
    const drift = el ? sample.playhead - el.currentTime : 0;

    this.panel.textContent = [
      `frames      ${(this.frames / seconds).toFixed(0)}/s   p95 ${p95.toFixed(1)}ms   worst ${this.worst.toFixed(0)}ms`,
      `long >50ms  ${this.longFrames} in ${seconds.toFixed(1)}s`,
      `react       ${(this.renders / seconds).toFixed(1)} commits/s`,
      `seeks       ${this.seeks} in ${seconds.toFixed(1)}s   clock ${sample.clockLeads ? "leads" : "follows"}`,
      el
        ? `element     t=${el.currentTime.toFixed(3)}  ready=${el.readyState}  ${el.seeking ? "SEEKING" : el.paused ? "paused" : "playing"}`
        : "element     none under playhead",
      el ? `drift       ${drift >= 0 ? "+" : ""}${(drift * 1000).toFixed(0)}ms (playhead − element)` : "",
      quality
        ? `decode      ${quality.droppedVideoFrames} dropped / ${quality.totalVideoFrames} frames`
        : "decode      (playback quality unavailable)",
      `audio       ${sample.audioState}`,
      video && video.videoWidth
        ? `source      ${video.videoWidth}×${video.videoHeight}`
        : "",
    ]
      .filter(Boolean)
      .join("\n");

    this.frames = 0;
    this.longFrames = 0;
    this.renders = 0;
    this.seeks = 0;
    this.worst = 0;
    this.durations = [];
    this.windowStart = now;
  }

  private readonly onSeeking = () => {
    this.seeks += 1;
  };

  destroy(): void {
    this.seekListenerOn?.removeEventListener("seeking", this.onSeeking);
    this.panel.remove();
  }
}
